import { useState, useCallback, useEffect, useRef } from 'react';
import { defaultAnalysisData, calculateUpcomingRefurbishment, calculateFictiveYear, getEffectiveYear, calculateRefurbishmentCosts } from '@/lib/scoring';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronLeft, ChevronRight, Save, Search, Elevator, Car, Layers, DoorOpen, Building2, Users, Flame, Check, Bookmark, AlertTriangle, Clock, CheckCircle2, Euro } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';

const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 17); }, [center, map]);
  return null;
}

const STEPS = [
  { title: 'Gebäudedaten', desc: 'Grundlegende Objektinformationen' },
  { title: 'Gebäudezustand', desc: 'Baujahr, Zustand und Modernisierungen' },
  { title: 'Verwaltungshistorie', desc: '' },
  { title: 'Kontakt & Adresse', desc: 'Kontaktdaten und Standort des Objekts' },
  { title: 'Zusammenfassung', desc: 'Überprüfen und absenden' },
];

const freqOptions = [
  { value: 'keine', label: 'Keine', color: 'bg-emerald-500' },
  { value: 'selten', label: 'Selten', color: 'bg-amber-500' },
  { value: 'haeufig', label: 'Häufig', color: 'bg-red-500' },
];
const levelOptions = [
  { value: 'niedrig', label: 'Niedrig', color: 'bg-emerald-500' },
  { value: 'mittel', label: 'Mittel', color: 'bg-amber-500' },
  { value: 'hoch', label: 'Hoch', color: 'bg-red-500' },
];
const reserveLevelOptions = [
  { value: 'hoch', label: 'Hoch', color: 'bg-emerald-500' },
  { value: 'mittel', label: 'Mittel', color: 'bg-amber-500' },
  { value: 'niedrig', label: 'Niedrig', color: 'bg-red-500' },
];
const sepaOptions = [
  { value: 'alle', label: 'Alle', color: 'bg-emerald-500' },
  { value: 'meiste', label: 'Meiste', color: 'bg-amber-500' },
  { value: 'wenige', label: 'Wenige', color: 'bg-red-500' },
];

const DEFAULT_RENOVATION_ITEMS = [
  { key: 'heating', label: 'Erneuerung der Heizungsanlage', required_equipment: 'has_central_heating' },
  { key: 'water_pipes', label: 'Erneuerung der Wasserleitungen' },
  { key: 'facade_paint', label: 'Fassadenanstrich' },
  { key: 'insulation', label: 'Erneuerung/Anbringen Wärmedämmung' },
  { key: 'basement_sealing', label: 'Erneuerung/Anbringen Kellerabdichtung' },
  { key: 'windows', label: 'Erneuerung aller Fenster' },
  { key: 'roof', label: 'Dachsanierung' },
  { key: 'elevator_modernization', label: 'Aufzugsmodernisierung', required_equipment: 'has_elevator' },
  { key: 'electrical', label: 'Erneuerung Elektroinstallation' },
  { key: 'balconies', label: 'Balkonsanierung' },
];

const DEFAULT_REPAIR_ITEMS = [
  { key: 'heating_repair', label: 'Heizungsreparatur', required_equipment: 'has_central_heating' },
  { key: 'elevator_repair', label: 'Aufzugsreparatur', required_equipment: 'has_elevator' },
  { key: 'roof_repair', label: 'Reparatur am Dach' },
  { key: 'stairwell_repair', label: 'Reparaturen im Treppenhaus' },
  { key: 'double_parker_repair', label: 'Mittelgroße Reparaturen an Doppelparkern', required_equipment: 'has_double_parkers' },
  { key: 'windows_repair', label: 'Reparaturen an Fenstern' },
  { key: 'facade_repair', label: 'Reparaturen an der Fassade' },
  { key: 'outdoor_repair', label: 'Reparaturen im Außenbereich' },
  { key: 'electrical_repair', label: 'Reparaturen an der Elektrik' },
  { key: 'water_pipes_repair', label: 'Reparaturen an Wasserleitungen' },
];

function SegmentedControl({ value, onChange, options, testId }) {
  return (
    <div className="flex border overflow-hidden" data-testid={testId}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)}
          className={`flex-1 px-3 py-2.5 text-sm font-medium transition-all duration-200 border-r last:border-r-0 ${
            value === opt.value ? `${opt.color} text-white` : 'bg-background text-muted-foreground hover:bg-secondary'
          }`} data-testid={`${testId}-${opt.value}`}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function HistoryRow({ label, value, onChange, testId, options }) {
  return (
    <div className="flex items-center justify-between gap-4 py-4 border-b border-dashed last:border-0">
      <span className="text-base leading-snug flex-1">{label}</span>
      <div className="shrink-0 w-52">
        <SegmentedControl value={value} onChange={onChange} options={options || freqOptions} testId={testId} />
      </div>
    </div>
  );
}

function FeatureCard({ label, checked, onChange, testId }) {
  return (
    <div className={`relative border-2 p-4 cursor-pointer transition-all duration-200 ${
      checked ? 'border-primary bg-primary/[0.03]' : 'border-border bg-secondary/40 hover:bg-secondary/70'
    }`} onClick={() => onChange(!checked)} data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-base font-medium select-none">{label}</span>
        <div className={`h-5 w-5 border-2 flex items-center justify-center transition-all duration-200 ${
          checked ? 'bg-primary border-primary' : 'border-border'
        }`}>
          {checked && <Check className="h-3 w-3 text-primary-foreground" />}
        </div>
      </div>
    </div>
  );
}

function RenovationGrid({ items, data, parent, onUpdate, testIdPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {items.map(item => (
        <div key={item.key} className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-all duration-150 ${
          data[parent]?.[item.key] ? 'bg-primary/[0.04] ring-1 ring-primary/20' : 'hover:bg-secondary/50'
        }`} onClick={() => onUpdate(parent, item.key, !data[parent]?.[item.key])} data-testid={`${testIdPrefix}-${item.key}`}>
          <div className={`h-5 w-5 border-2 flex items-center justify-center shrink-0 transition-all ${
            data[parent]?.[item.key] ? 'bg-primary border-primary' : 'border-border'
          }`}>
            {data[parent]?.[item.key] && <Check className="h-3 w-3 text-primary-foreground" />}
          </div>
          <span className="text-base select-none">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function AnalysisWizard({ initialData, onSubmit, onDataChange, onAutoSave, analysisId, refurbishmentThresholds, refurbishmentThresholdsList, repairItems, costEstimates, modernizationItems, equipmentItems, isAdmin, isEdit, loading }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(() => ({ ...defaultAnalysisData, ...(initialData || {}) }));
  const [geocoding, setGeocoding] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autoSaveTimer = useRef(null);
  const addressSearchTimer = useRef(null);
  const wizardRef = useRef(null);

  // Scroll to top when step changes
  useEffect(() => {
    wizardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const update = useCallback((field, value) => {
    setData(prev => {
      const next = { ...prev, [field]: value };
      onDataChange?.(next);
      return next;
    });
  }, [onDataChange]);

  const updateNested = useCallback((parent, field, value) => {
    setData(prev => {
      const next = { ...prev, [parent]: { ...prev[parent], [field]: value } };
      onDataChange?.(next);
      return next;
    });
  }, [onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!onAutoSave) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      onAutoSave(data);
    }, 2000);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [data, onAutoSave]);

  // Address autocomplete search
  const searchAddress = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', Germany')}&limit=5&addressdetails=1`);
      const results = await res.json();
      setAddressSuggestions(results.map(r => ({
        display: r.display_name,
        address: r.address?.road || r.address?.pedestrian || '',
        houseNumber: r.address?.house_number || '',
        city: r.address?.city || r.address?.town || r.address?.village || '',
        zip: r.address?.postcode || '',
        lat: parseFloat(r.lat),
        lon: parseFloat(r.lon),
      })));
    } catch (e) {
      console.error('Address search error', e);
    }
  }, []);

  const handleAddressInput = (field, value) => {
    update(field, value);
    if (addressSearchTimer.current) clearTimeout(addressSearchTimer.current);
    addressSearchTimer.current = setTimeout(() => {
      searchAddress(value);
      setShowSuggestions(true);
    }, 300);
  };

  const selectAddressSuggestion = (suggestion) => {
    const streetWithNumber = suggestion.houseNumber 
      ? `${suggestion.address} ${suggestion.houseNumber}` 
      : suggestion.address;
    if (!data.name) update('name', streetWithNumber);
    update('address', streetWithNumber);
    update('city', suggestion.city);
    update('zip_code', suggestion.zip);
    update('latitude', suggestion.lat);
    update('longitude', suggestion.lon);
    if (isAdmin && suggestion.lat && suggestion.lon) {
      const R = 6371;
      const dLat = (suggestion.lat - 50.0782) * Math.PI / 180;
      const dLon = (suggestion.lon - 8.2398) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(50.0782*Math.PI/180)*Math.cos(suggestion.lat*Math.PI/180)*Math.sin(dLon/2)**2;
      const dist = R * 2 * Math.asin(Math.sqrt(a));
      update('office_distance_km', Math.round(dist * 10) / 10);
    }
    setAddressSuggestions([]);
    setShowSuggestions(false);
  };

  const geocodeAddress = async () => {
    if (!data.address || !data.city) return;
    setGeocoding(true);
    try {
      const q = `${data.address}, ${data.zip_code} ${data.city}, Germany`;
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
      const results = await res.json();
      if (results.length > 0) {
        const lat = parseFloat(results[0].lat);
        const lon = parseFloat(results[0].lon);
        update('latitude', lat);
        update('longitude', lon);
        if (isAdmin) {
          const R = 6371;
          const dLat = (lat - 50.0782) * Math.PI / 180;
          const dLon = (lon - 8.2398) * Math.PI / 180;
          const a = Math.sin(dLat/2)**2 + Math.cos(50.0782*Math.PI/180)*Math.cos(lat*Math.PI/180)*Math.sin(dLon/2)**2;
          const dist = R * 2 * Math.asin(Math.sqrt(a));
          update('office_distance_km', Math.round(dist * 10) / 10);
        }
      }
    } catch (e) { console.error('Geocoding error', e); }
    setGeocoding(false);
  };

  const progress = ((step + 1) / STEPS.length) * 100;
  const currentYear = new Date().getFullYear();
  const renovationThreshold = currentYear - 15;
  
  // Check if there are planned renovations with costs
  const plannedRenovationsWithCosts = data.planned_renovations_cost > 0 && 
    Object.values(data.planned_renovations || {}).some(v => v === true);
  
  // Calculate upcoming refurbishment needs, excluding items that are already planned
  const upcomingNeeds = calculateUpcomingRefurbishment(
    data, 
    refurbishmentThresholds, 
    refurbishmentThresholdsList, 
    plannedRenovationsWithCosts // exclude planned renovations if they have costs
  );
  const refurbishmentCosts = calculateRefurbishmentCosts(upcomingNeeds, data.units, costEstimates);
  
  // Get planned renovations that would have been in the refurbishment list (for display)
  const plannedRefurbishmentItems = plannedRenovationsWithCosts 
    ? calculateUpcomingRefurbishment(data, refurbishmentThresholds, refurbishmentThresholdsList, false).needs
        .filter(need => (data.planned_renovations || {})[need.key])
    : [];

  // Use modernization items from settings or default
  const activeModernizationItems = (modernizationItems && modernizationItems.length > 0)
    ? modernizationItems.filter(r => r.active !== false).map(r => ({ key: r.key, label: r.label, required_equipment: r.required_equipment }))
    : DEFAULT_RENOVATION_ITEMS;

  // Filter modernization items by equipment availability
  const filteredModernizationItems = activeModernizationItems.filter(r => {
    if (!r.required_equipment) return true;
    return data[r.required_equipment];
  });

  // Use repair items from settings or default
  const activeRepairItems = (repairItems && repairItems.length > 0)
    ? repairItems.filter(r => r.active !== false).map(r => ({ key: r.key, label: r.label, required_equipment: r.required_equipment }))
    : DEFAULT_REPAIR_ITEMS;

  // Filter repair items by equipment availability
  const filteredRepairItems = activeRepairItems.filter(r => {
    if (!r.required_equipment) return true;
    return data[r.required_equipment];
  });

  // Building age and fictive year
  const fictiveYear = calculateFictiveYear(data.construction_year, data.conversion_year);
  const effectiveYear = getEffectiveYear(data);
  const buildingAge = currentYear - effectiveYear;
  const realAge = currentYear - (data.construction_year || currentYear);
  const hasFictiveYear = !!fictiveYear || !!data.fictive_year_override;

  // Per-step completeness
  const stepCompleteness = [
    // Step 0: Gebäudedaten
    (() => {
      let filled = 0, total = 3;
      if (data.name) filled++;
      if (data.units > 1) filled++;
      if (data.construction_year && data.construction_year !== 1990) filled++;
      return Math.round((filled / total) * 100);
    })(),
    // Step 1: Gebäudezustand
    (() => {
      const renCount = Object.entries(data.renovations || {}).filter(([k, v]) => k !== 'other' && v).length;
      const repairCount = Object.entries(data.major_repairs || {}).filter(([k, v]) => k !== 'other' && v).length;
      let filled = 0, total = 3;
      if (data.general_condition && data.general_condition !== 'gut') filled++;
      else filled += 0.5;
      if (renCount > 0 || data.renovations?.other) filled++;
      if (repairCount > 0 || data.major_repairs?.other) filled++;
      return Math.round((filled / total) * 100);
    })(),
    // Step 2: Verwaltungshistorie
    (() => {
      const h = data.history || {};
      const numFields = ['extraordinary_meetings', 'ownership_changes', 'payment_defaults', 'disturbances', 'tenant_changes', 'special_assessments', 'court_proceedings', 'insurance_claims', 'circular_resolutions'];
      let touched = 0;
      numFields.forEach(f => { if (h[f] !== 0 && h[f] !== 'keine') touched++; });
      if (h.reserves_level !== 'mittel') touched++;
      if (h.owner_information_needs !== 'mittel') touched++;
      return Math.round((touched / 11) * 100);
    })(),
    // Step 3: Kontakt & Adresse
    (() => {
      let filled = 0, total = 4;
      if (data.contact_name) filled++;
      if (data.contact_email) filled++;
      if (data.address) filled++;
      if (data.city) filled++;
      return Math.round((filled / total) * 100);
    })(),
    100, // Step 4: Summary (always complete)
  ];

  // Size category based on total units (not per building)
  const getSizeCategory = (totalUnits) => {
    if (totalUnits <= 3) return { name: 'Kleinstgemeinschaft', color: 'bg-red-100 text-red-800 border border-red-200' };
    if (totalUnits <= 6) return { name: 'kleine Gemeinschaft', color: 'bg-amber-100 text-amber-800 border border-amber-200' };
    if (totalUnits <= 15) return { name: 'Kleine WEG', color: 'bg-yellow-100 text-yellow-800 border border-yellow-200' };
    if (totalUnits <= 30) return { name: 'Mittlere WEG', color: 'bg-blue-100 text-blue-800 border border-blue-200' };
    if (totalUnits <= 60) return { name: 'Große WEG', color: 'bg-cyan-100 text-cyan-800 border border-cyan-200' };
    if (totalUnits <= 100) return { name: 'Sehr große WEG', color: 'bg-emerald-100 text-emerald-800 border border-emerald-200' };
    return { name: 'Großanlage', color: 'bg-violet-100 text-violet-800 border border-violet-200' };
  };
  const sizeCategory = getSizeCategory(data.units);

  // Filter planned renovations to exclude already completed ones
  const availablePlannedItems = filteredModernizationItems.filter(item => !data.renovations?.[item.key]);

  return (
    <div ref={wizardRef} className="space-y-6 max-w-5xl mx-auto" data-testid="analysis-wizard">
      {/* Analysis ID display */}
      {analysisId && (
        <div className="flex justify-end">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/20" data-testid="analysis-id-display">
            <span className="text-sm text-muted-foreground">Analyse-ID:</span>
            <span className="text-base font-mono font-semibold text-primary">{analysisId}</span>
          </div>
        </div>
      )}
      
      <div className="flex items-center gap-3">
        {STEPS.map((s, i) => (
          <button key={i} onClick={() => setStep(i)} className={`flex items-center gap-2 text-sm font-medium transition-colors duration-150 ${i === step ? 'text-foreground' : i < step ? 'text-[hsl(var(--brand-blue))]' : 'text-muted-foreground'}`} data-testid={`wizard-step-${i}`}>
            <span className={`relative h-8 w-8 flex items-center justify-center text-sm font-bold border-2 transition-all ${i === step ? 'bg-primary text-primary-foreground border-primary' : i < step ? 'bg-[hsl(var(--brand-blue))] text-white border-[hsl(var(--brand-blue))]' : 'border-border'}`}>
              {i + 1}
              {i < 4 && stepCompleteness[i] > 0 && i !== step && (
                <span className={`absolute -top-1 -right-1 h-3 w-3 rounded-full text-[7px] font-bold flex items-center justify-center ${stepCompleteness[i] >= 100 ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-white'}`} data-testid={`step-${i}-indicator`} />
              )}
            </span>
            <span className="hidden sm:inline">{s.title}</span>
          </button>
        ))}
      </div>
      <Progress value={progress} className="h-2" data-testid="wizard-progress" />

      <div className="wizard-step-enter" key={step}>
        <div className="mb-8">
          <h2 className="text-2xl font-bold tracking-tight">{STEPS[step].title}</h2>
          <p className="text-base text-muted-foreground mt-1">{STEPS[step].desc}</p>
        </div>

        {step === 0 && (
          <div className="space-y-8">
            {isAdmin && (
              <div className="flex items-center gap-3 p-4 bg-[hsl(var(--brand-blue))]/5 border border-[hsl(var(--brand-blue))]/20">
                <Bookmark className="h-5 w-5 text-[hsl(var(--brand-blue))]" />
                <Label htmlFor="portfolio" className="text-base cursor-pointer flex-1">Als Bestandsobjekt kennzeichnen</Label>
                <Switch id="portfolio" checked={data.is_portfolio_property} onCheckedChange={v => update('is_portfolio_property', v)} data-testid="switch-portfolio" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Bezeichnung der WEG</Label>
              <Input 
                id="name" 
                value={data.name} 
                onChange={e => update('name', e.target.value)} 
                placeholder="z.B. WEG Musterstraße 1, 65189 Wiesbaden" 
                className="h-12 text-base" 
                data-testid="input-name" 
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Anzahl Einheiten</Label>
                <Input 
                  type="number" 
                  min="1" 
                  max="500" 
                  value={data.units} 
                  onChange={e => update('units', Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))} 
                  className="w-24 h-10 text-xl font-bold text-right" 
                  data-testid="input-units-manual"
                />
              </div>
              <Slider value={[data.units]} onValueChange={v => update('units', v[0])} min={2} max={200} step={1} className="py-2" data-testid="slider-units" />
              <div className="flex justify-between text-xs text-muted-foreground uppercase tracking-wider"><span>2</span><span>50</span><span>100</span><span>200</span></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="buildings" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Gebäude/Gebäudeteile/Treppenhäuser</Label>
                <Input id="buildings" type="number" min="1" value={data.number_of_buildings} onChange={e => update('number_of_buildings', Math.max(1, parseInt(e.target.value) || 1))} className="h-12 text-base" data-testid="input-buildings" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Größenkategorie</Label>
                <div className={`h-12 flex items-center px-3 rounded-md text-base font-semibold ${sizeCategory.color}`} data-testid="size-category">
                  {sizeCategory.name}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4 block">Ausstattung & Besonderheiten</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Built-in equipment items (always shown) */}
                <FeatureCard label="Personenaufzug" checked={data.has_elevator} onChange={v => update('has_elevator', v)} testId="feat-elevator" />
                <FeatureCard label="Tiefgarage" checked={data.has_underground_garage} onChange={v => update('has_underground_garage', v)} testId="feat-garage" />
                <FeatureCard label="Doppelparker/Hebebühnen" checked={data.has_double_parkers} onChange={v => update('has_double_parkers', v)} testId="feat-parkers" />
                <FeatureCard label="Untergemeinschaften (sep. Abrechnung)" checked={data.has_sub_communities} onChange={v => update('has_sub_communities', v)} testId="feat-subcommunities" />
                <FeatureCard label="Zentrale Heizungsanlage" checked={data.has_central_heating} onChange={v => update('has_central_heating', v)} testId="feat-heating" />
                {/* Dynamic custom equipment items from settings */}
                {(equipmentItems || [])
                  .filter(item => item.active !== false && item.key?.startsWith('custom_'))
                  .map(item => (
                    <FeatureCard
                      key={item.key}
                      label={item.label}
                      checked={data.custom_equipment?.[item.key] || false}
                      onChange={v => updateNested('custom_equipment', item.key, v)}
                      testId={`feat-${item.key}`}
                    />
                  ))}
              </div>
            </div>

            {isAdmin && (
              <div className="space-y-4 p-4 bg-secondary/30 border border-dashed">
                <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Admin-Einstellungen</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="custom_base_fee" className="text-base">Individuelles Grundhonorar (EUR/Einheit)</Label>
                    <Input id="custom_base_fee" type="number" step="0.01" value={data.custom_base_fee || ''} onChange={e => update('custom_base_fee', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Aus Vergütungsskala" className="h-12 text-base" data-testid="input-custom-fee" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="office_distance" className="text-base">Entfernung zum Büro (km)</Label>
                    <Input id="office_distance" type="number" step="0.1" value={data.office_distance_km || ''} onChange={e => update('office_distance_km', e.target.value ? parseFloat(e.target.value) : null)} placeholder="Auto bei Geocodierung" data-testid="input-distance" />
                    <p className="text-[10px] text-muted-foreground">Büro: Wiesbaden. {'>'} 20 km = Abschlag</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="construction_year" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Baujahr</Label>
                <Input id="construction_year" type="number" value={data.construction_year} onChange={e => update('construction_year', parseInt(e.target.value) || currentYear)} className="h-12 text-base" data-testid="input-construction-year" />
                {realAge > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary border text-sm" data-testid="building-age-badge">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-medium">{realAge} Jahre alt</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="conversion_year" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Umwandlung in WEG (falls abweichend)</Label>
                <Input id="conversion_year" type="number" value={data.conversion_year || ''} onChange={e => update('conversion_year', e.target.value ? parseInt(e.target.value) : null)} placeholder="Optional" className="h-12 text-base" data-testid="input-conversion-year" />
              </div>
            </div>

            {hasFictiveYear && (
              <div className="p-4 border-2 border-blue-200 bg-blue-50/50 space-y-3" data-testid="fictive-year-section">
                <div>
                  <Label className="text-base font-semibold text-blue-800">Fiktives Baujahr</Label>
                  <p className="text-sm text-blue-600 mt-0.5">
                    Bei der Umwandlung {data.conversion_year} wurden typischerweise Heizung, Elektrik und Fenster erneuert. 
                    Das fiktive Baujahr berücksichtigt diese Verjüngung und wird für alle Berechnungen verwendet.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                  <div>
                    <span className="text-xs text-blue-500 uppercase font-semibold">Berechnet</span>
                    <div className="h-10 flex items-center px-3 bg-blue-100 border border-blue-200 text-base font-semibold text-blue-800" data-testid="fictive-year-calculated">
                      {fictiveYear || '–'}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-blue-500 uppercase font-semibold">Überschreiben (optional)</span>
                    <Input
                      type="number"
                      value={data.fictive_year_override || ''}
                      onChange={e => update('fictive_year_override', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder={fictiveYear ? String(fictiveYear) : ''}
                      className="h-10 text-base"
                      data-testid="input-fictive-year-override"
                    />
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-2 bg-blue-100 border border-blue-200 text-sm" data-testid="effective-age-badge">
                      <Clock className="h-3.5 w-3.5 text-blue-600" />
                      <span className="font-semibold text-blue-800">Effektiv: {buildingAge} Jahre alt</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(currentYear - data.construction_year) < 6 && (
              <div className="flex items-center justify-between p-4 border-2 border-amber-200 bg-amber-50/50" data-testid="defects-section">
                <div>
                  <Label htmlFor="defects" className="text-base cursor-pointer font-semibold text-amber-800">Baumängel vorhanden?</Label>
                  <p className="text-sm text-amber-600 mt-0.5">Bei Neubauten unter 6 Jahren relevant</p>
                </div>
                <Switch id="defects" checked={data.has_construction_defects} onCheckedChange={v => update('has_construction_defects', v)} data-testid="switch-defects" />
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">Allgemeiner Zustand von Grundstück und Gebäude</Label>
              <p className="text-sm text-muted-foreground mb-3">Einschätzung, ob bisher nicht festgestellter Instandhaltungsbedarf entstehen könnte</p>
              <RadioGroup value={data.general_condition} onValueChange={v => update('general_condition', v)} className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="general-condition-group">
                {[
                  { value: 'sehr_gut', label: 'Sehr gut', desc: 'Kein Bedarf erwartet', ring: 'ring-emerald-300' },
                  { value: 'gut', label: 'Gut', desc: 'Normaler Zustand', ring: 'ring-blue-300' },
                  { value: 'mittel', label: 'Mittel', desc: 'Bedarf möglich', ring: 'ring-amber-300' },
                  { value: 'schlecht', label: 'Schlecht', desc: 'Bedarf wahrscheinlich', ring: 'ring-red-300' },
                ].map(opt => (
                  <div key={opt.value} className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${data.general_condition === opt.value ? `bg-primary/[0.03] border-primary ring-2 ${opt.ring}` : 'border-border bg-secondary/40 hover:bg-secondary/60'}`} onClick={() => update('general_condition', opt.value)} data-testid={`condition-${opt.value}`}>
                    <RadioGroupItem value={opt.value} id={`gc-${opt.value}`} className="sr-only" />
                    <span className="text-base font-semibold">{opt.label}</span>
                    <span className="text-xs text-muted-foreground text-center leading-tight">{opt.desc}</span>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">Umfangreiche Modernisierungen innerhalb der letzten 15 Jahre, also ungefähr seit {renovationThreshold}</Label>
              <RenovationGrid items={filteredModernizationItems} data={data} parent="renovations" onUpdate={updateNested} testIdPrefix="ren" />
              <div className="mt-4 space-y-2">
                <Label htmlFor="ren-other" className="text-base">Sonstige Modernisierungen</Label>
                <Input id="ren-other" value={data.renovations?.other || ''} onChange={e => updateNested('renovations', 'other', e.target.value)} placeholder="z.B. Außenanlagen, Spielplatz, Briefkastenanlage" className="h-12 text-base" data-testid="input-ren-other" />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">Größere Reparaturen innerhalb der letzten Jahre</Label>
              <RenovationGrid items={filteredRepairItems} data={data} parent="major_repairs" onUpdate={updateNested} testIdPrefix="repair" />
              <div className="mt-4 space-y-2">
                <Label htmlFor="repair-other" className="text-base">Sonstige Reparaturen</Label>
                <Input id="repair-other" value={data.major_repairs?.other || ''} onChange={e => updateNested('major_repairs', 'other', e.target.value)} placeholder="z.B. Wasserrohrbruch, Sturmschäden" className="h-12 text-base" data-testid="input-repair-other" />
              </div>
            </div>

            {availablePlannedItems.length > 0 && (
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">Geplante oder anstehende Modernisierungen</Label>
                <p className="text-sm text-muted-foreground mb-3">Gibt es Ideen oder Vorhaben, weitere Modernisierungen durchzuführen?</p>
                <RenovationGrid items={availablePlannedItems} data={data} parent="planned_renovations" onUpdate={updateNested} testIdPrefix="planned" />
                <div className="mt-4 space-y-2">
                  <Label htmlFor="planned-notes" className="text-base">Ergänzende Hinweise</Label>
                  <Textarea id="planned-notes" value={data.planned_renovations_notes || ''} onChange={e => update('planned_renovations_notes', e.target.value)} placeholder="Weitere Details zu geplanten Maßnahmen" rows={2} className="text-base" data-testid="input-planned-notes" />
                </div>
                
                {/* Show cost field if any planned renovation is selected */}
                {(Object.values(data.planned_renovations || {}).some(v => v === true) || data.planned_renovations_notes) && (
                  <div className="mt-6 p-5 bg-blue-50 border-2 border-blue-200" data-testid="renovation-cost-section">
                    <Label className="text-base font-semibold text-blue-800 mb-3 block">Kostenumfang der Modernisierungen</Label>
                    <p className="text-sm text-blue-600 mb-4">
                      Geben Sie den geschätzten Gesamtkostenumfang der geplanten Maßnahmen an.
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Geschätzter Kostenumfang:</span>
                        <span className="text-2xl font-bold text-blue-700 tabular-nums" data-testid="renovation-cost-display">
                          {(data.planned_renovations_cost || 0).toLocaleString('de-DE')} €
                        </span>
                      </div>
                      <Slider
                        value={[data.planned_renovations_cost || 0]}
                        onValueChange={v => update('planned_renovations_cost', v[0])}
                        min={0}
                        max={2000000}
                        step={10000}
                        className="py-2"
                        data-testid="slider-renovation-cost"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>0 €</span>
                        <span>250.000 €</span>
                        <span>500.000 €</span>
                        <span>1 Mio €</span>
                        <span>2 Mio €</span>
                      </div>
                      <Input
                        type="number"
                        min={0}
                        value={data.planned_renovations_cost || ''}
                        onChange={e => update('planned_renovations_cost', parseInt(e.target.value) || 0)}
                        placeholder="Oder Betrag manuell eingeben"
                        className="h-10 text-base"
                        data-testid="input-renovation-cost"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {availablePlannedItems.length === 0 && (
              <div className="p-4 bg-emerald-50 border border-emerald-200">
                <p className="text-sm text-emerald-700">Alle Modernisierungen wurden bereits durchgeführt. Es stehen keine weiteren Maßnahmen zur Planung an.</p>
              </div>
            )}

            {/* Display planned renovations with user-entered costs */}
            {plannedRefurbishmentItems.length > 0 && (
              <div className="p-5 border-2 border-blue-200 bg-blue-50/30" data-testid="planned-refurbishment-section">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-semibold text-blue-800">Bereits geplante Maßnahmen</Label>
                </div>
                <p className="text-sm text-blue-700 mb-4">
                  Die folgenden Maßnahmen sind bereits geplant und werden nicht erneut im Sanierungsbedarf kalkuliert:
                </p>
                <div className="space-y-2">
                  {plannedRefurbishmentItems.map(item => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-blue-100" data-testid={`planned-refurb-${item.key}`}>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-blue-600" />
                        <span className="text-base font-medium">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-muted-foreground">Alter: {item.age} J. (Schwelle: {item.threshold} J.)</span>
                        <span className="px-2 py-1 text-xs font-semibold bg-blue-200 text-blue-800">
                          Geplant
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-blue-100 border border-blue-300" data-testid="planned-cost-summary">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-blue-800">Vom Nutzer angegebene Kosten:</span>
                    <span className="text-lg font-bold text-blue-900">{(data.planned_renovations_cost || 0).toLocaleString('de-DE')} €</span>
                  </div>
                  {isAdmin && (
                    <p className="text-xs text-blue-600 mt-1">
                      Zusätzliche Verwaltervergütung ({data.renovation_fee_percent || 5}%): <strong>{Math.round((data.planned_renovations_cost || 0) * (data.renovation_fee_percent || 5) / 100).toLocaleString('de-DE')} €/Jahr</strong>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Upcoming Refurbishment Needs */}
            {upcomingNeeds.count > 0 && (
              <div className="p-5 border-2 border-amber-200 bg-amber-50/30" data-testid="upcoming-needs-section">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <Label className="text-base font-semibold text-amber-800">Kommender Sanierungsbedarf</Label>
                </div>
                <p className="text-sm text-amber-700 mb-4">
                  Basierend auf dem Gebäudealter und den bisherigen Modernisierungen könnten folgende Maßnahmen in den nächsten Jahren anstehen:
                </p>
                <div className="space-y-2">
                  {upcomingNeeds.needs.map(need => {
                    const costItem = refurbishmentCosts.items.find(c => c.key === need.key);
                    return (
                      <div key={need.key} className={`flex items-center justify-between p-3 ${need.urgency === 'hoch' ? 'bg-red-100' : 'bg-amber-100'}`} data-testid={`upcoming-${need.key}`}>
                        <div className="flex items-center gap-2">
                          <Clock className={`h-4 w-4 ${need.urgency === 'hoch' ? 'text-red-600' : 'text-amber-600'}`} />
                          <span className="text-base font-medium">{need.label}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          {costItem && (
                            <span className="font-semibold text-amber-900" data-testid={`cost-${need.key}`}>
                              ca. {costItem.cost.toLocaleString('de-DE')} €
                            </span>
                          )}
                          <span className="text-muted-foreground">Alter: {need.age} J. (Schwelle: {need.threshold} J.)</span>
                          <span className={`px-2 py-1 text-xs font-semibold ${need.urgency === 'hoch' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                            {need.urgency === 'hoch' ? 'Dringend' : 'Mittelfristig'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {refurbishmentCosts.totalCost > 0 && (
                  <div className="mt-4 p-3 bg-amber-100 border border-amber-300" data-testid="refurbishment-total-cost">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold text-amber-800">Geschätzte Gesamtkosten Sanierungsbedarf:</span>
                      <span className="text-lg font-bold text-amber-900">{refurbishmentCosts.totalCost.toLocaleString('de-DE')} €</span>
                    </div>
                    {isAdmin && (
                      <p className="text-xs text-amber-600 mt-1">
                        Geschätzte zusätzliche Verwaltervergütung ({data.renovation_fee_percent || 5}%): <strong>{Math.round(refurbishmentCosts.totalCost * (data.renovation_fee_percent || 5) / 100).toLocaleString('de-DE')} €</strong>
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-amber-600 mt-3">
                  Diese Einschätzung basiert auf typischen Lebensdauern von Bauteilen. Die tatsächliche Notwendigkeit kann je nach Wartungszustand variieren.
                </p>
              </div>
            )}

            {/* Total Investment and Reserve Question */}
            {((data.planned_renovations_cost > 0) || (refurbishmentCosts.totalCost > 0)) && (
              <div className="p-5 border-2 border-emerald-200 bg-emerald-50/30" data-testid="total-investment-section">
                <div className="flex items-center gap-2 mb-3">
                  <Euro className="h-5 w-5 text-emerald-600" />
                  <Label className="text-base font-semibold text-emerald-800">Geschätzte Gesamtinvestition</Label>
                </div>
                <div className="space-y-2 mb-4">
                  {data.planned_renovations_cost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-700">Geplante Modernisierungen:</span>
                      <span className="font-medium">{(data.planned_renovations_cost || 0).toLocaleString('de-DE')} €</span>
                    </div>
                  )}
                  {refurbishmentCosts.totalCost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-700">Kommender Sanierungsbedarf:</span>
                      <span className="font-medium">{refurbishmentCosts.totalCost.toLocaleString('de-DE')} €</span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="flex justify-between">
                    <span className="font-semibold text-emerald-800">Summe:</span>
                    <span className="text-xl font-bold text-emerald-900" data-testid="total-investment-amount">
                      {((data.planned_renovations_cost || 0) + (refurbishmentCosts.totalCost || 0)).toLocaleString('de-DE')} €
                    </span>
                  </div>
                </div>
                
                <div className="p-4 bg-white border border-emerald-200 rounded-md">
                  <p className="text-sm font-medium text-emerald-800 mb-3">
                    Verfügt die Wohnungseigentümergemeinschaft über mindestens diese Erhaltungsrücklage?
                  </p>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={data.has_sufficient_reserves === true ? "default" : "outline"}
                      className={`flex-1 ${data.has_sufficient_reserves === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                      onClick={() => update('has_sufficient_reserves', true)}
                      data-testid="reserves-yes-btn"
                    >
                      Ja
                    </Button>
                    <Button
                      type="button"
                      variant={data.has_sufficient_reserves === false ? "default" : "outline"}
                      className={`flex-1 ${data.has_sufficient_reserves === false ? 'bg-red-600 hover:bg-red-700' : ''}`}
                      onClick={() => update('has_sufficient_reserves', false)}
                      data-testid="reserves-no-btn"
                    >
                      Nein
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => update('has_sufficient_reserves', null)}
                      data-testid="reserves-unknown-btn"
                    >
                      Keine Angabe
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div className="p-4 bg-secondary/50 border mb-4">
              <p className="text-base text-muted-foreground">
                Beurteilen Sie welche Vorkommnisse die Gemeinschaft in den letzten 5 Jahren hatte.
              </p>
              <ul className="mt-2 text-sm text-muted-foreground space-y-1">
                <li><strong>Häufig</strong> = regelmäßig vorgekommen (2-5x in 5 Jahren)</li>
                <li><strong>Selten</strong> = vereinzelt vorgekommen (1-2x in 5 Jahren)</li>
                <li><strong>Keine</strong> = nicht vorgekommen</li>
              </ul>
            </div>
            <HistoryRow label="Außerordentliche Eigentümerversammlungen" value={data.history.extraordinary_meetings} onChange={v => updateNested('history', 'extraordinary_meetings', v)} testId="freq-meetings" />
            <HistoryRow label="Schriftliche Umlauf- bzw. Beschlussfassungen" value={data.history.circular_resolutions} onChange={v => updateNested('history', 'circular_resolutions', v)} testId="freq-circular" />
            <HistoryRow label="Eigentumswechsel" value={data.history.ownership_changes} onChange={v => updateNested('history', 'ownership_changes', v)} testId="freq-ownership" />
            <HistoryRow label="Zahlungsverzug von Hausgeldzahlungen" value={data.history.payment_defaults} onChange={v => updateNested('history', 'payment_defaults', v)} testId="freq-payment" />
            <HistoryRow label="Abmahnungen wegen Störungen" value={data.history.disturbances} onChange={v => updateNested('history', 'disturbances', v)} testId="freq-disturbances" />
            <HistoryRow label="Mieterwechsel" value={data.history.tenant_changes} onChange={v => updateNested('history', 'tenant_changes', v)} testId="freq-tenants" />
            <HistoryRow label="Sonderumlagen" value={data.history.special_assessments} onChange={v => updateNested('history', 'special_assessments', v)} testId="freq-assessments" />
            <HistoryRow label="Gerichtsverfahren" value={data.history.court_proceedings} onChange={v => updateNested('history', 'court_proceedings', v)} testId="freq-court" />
            <HistoryRow label="Versicherungsschäden" value={data.history.insurance_claims} onChange={v => updateNested('history', 'insurance_claims', v)} testId="freq-insurance" />
            <Separator className="my-4" />
            <HistoryRow label="Rücklagenniveau" value={data.history.reserves_level} onChange={v => updateNested('history', 'reserves_level', v)} testId="level-reserves" options={reserveLevelOptions} />
            
            {/* Show reserve status if answered (including "keine Angabe") */}
            {(data.has_sufficient_reserves === true || data.has_sufficient_reserves === false) && (
              <div className={`p-4 rounded-md border ${data.has_sufficient_reserves ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="reserves-status-info">
                <div className="flex items-start gap-2">
                  {data.has_sufficient_reserves ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800">Ausreichende Erhaltungsrücklage vorhanden</p>
                        <p className="text-xs text-emerald-600 mt-1">Für geplante u. notwendige Maßnahmen ausreichend Kapitalvermögen vorhanden. Dies wirkt sich positiv auf die Gesamtbewertung aus.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Erhaltungsrücklage nicht ausreichend</p>
                        <p className="text-xs text-red-600 mt-1">Für geplante u. notwendige Maßnahmen ist das Kapitalvermögen nicht ausreichend. Dies wirkt sich auf die Gesamtbewertung aus.</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            
            <HistoryRow label="Informationsbedürfnis der Eigentümer" value={data.history.owner_information_needs} onChange={v => updateNested('history', 'owner_information_needs', v)} testId="level-info" options={levelOptions} />
            <HistoryRow label="SEPA-Lastschriftverfahren Teilnahme" value={data.history.sepa_participation} onChange={v => updateNested('history', 'sepa_participation', v)} testId="sepa-participation" options={sepaOptions} />
            <Separator className="my-4" />
            <div className="space-y-3">
              <FeatureCard label="Professioneller Hausmeisterdienst vorhanden" checked={data.history.has_professional_caretaker} onChange={v => updateNested('history', 'has_professional_caretaker', v)} testId="switch-caretaker" />
              <FeatureCard label="Funktionierender Verwaltungsbeirat vorhanden" checked={data.has_advisory_board} onChange={v => update('has_advisory_board', v)} testId="feat-advisory-board" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            {/* Adresse zuerst */}
            <div className="space-y-2 relative">
              <Label htmlFor="address" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Straße und Hausnummer</Label>
              <div className="relative">
                <Input 
                  id="address" 
                  value={data.address} 
                  onChange={e => handleAddressInput('address', e.target.value)} 
                  onFocus={() => data.address?.length >= 3 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Musterstraße 1 (Adresse eingeben für Vorschläge)" 
                  className="h-12 text-base" 
                  data-testid="input-address" 
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              </div>
              {showSuggestions && addressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border shadow-lg max-h-60 overflow-auto">
                  {addressSuggestions.map((s, i) => (
                    <button
                      key={i}
                      className="w-full px-4 py-3 text-left hover:bg-secondary text-base border-b last:border-0"
                      onMouseDown={() => selectAddressSuggestion(s)}
                    >
                      {s.display}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zip_code" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">PLZ</Label>
                <Input id="zip_code" value={data.zip_code} onChange={e => update('zip_code', e.target.value)} placeholder="12345" className="h-12 text-base" data-testid="input-zip" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="city" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Stadt</Label>
                <Input id="city" value={data.city} onChange={e => update('city', e.target.value)} placeholder="Berlin" className="h-12 text-base" data-testid="input-city" />
              </div>
            </div>
            <Button variant="outline" className="gap-2 h-12 text-base" onClick={geocodeAddress} disabled={geocoding || (!data.address && !data.city)} data-testid="geocode-btn">
              {geocoding ? <span className="animate-pulse">Suche...</span> : <><Search className="h-5 w-5" /> Adresse auf Karte anzeigen</>}
            </Button>
            {data.latitude && data.longitude && (
              <Card className="overflow-hidden border shadow-md">
                <CardContent className="p-0">
                  <MapContainer center={[data.latitude, data.longitude]} zoom={17} style={{ height: '300px', width: '100%' }} scrollWheelZoom={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
                    <Marker position={[data.latitude, data.longitude]} icon={markerIcon} />
                    <MapUpdater center={[data.latitude, data.longitude]} />
                  </MapContainer>
                </CardContent>
              </Card>
            )}
            
            <Separator />
            
            {/* Ansprechpartner danach */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Ansprechpartner</Label>
                <Input id="contact_name" value={data.contact_name} onChange={e => update('contact_name', e.target.value)} placeholder="Vor- und Nachname" className="h-12 text-base" data-testid="input-contact-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Telefon</Label>
                <Input id="contact_phone" value={data.contact_phone} onChange={e => update('contact_phone', e.target.value)} placeholder="+49 ..." className="h-12 text-base" data-testid="input-contact-phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">E-Mail</Label>
              <Input id="contact_email" type="email" value={data.contact_email} onChange={e => update('contact_email', e.target.value)} placeholder="email@beispiel.de" className="h-12 text-base" data-testid="input-contact-email" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6" data-testid="summary-step">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    Gebäudedaten
                    <button onClick={() => setStep(0)} className="ml-auto text-xs text-[hsl(var(--brand-blue))] hover:underline" data-testid="summary-edit-0">Bearbeiten</button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">WEG</span><span className="font-medium">{data.name || '–'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Einheiten</span><span className="font-medium">{data.units}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Gebäude</span><span className="font-medium">{data.number_of_buildings}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Größenkategorie</span><span className="font-medium">{sizeCategory.name}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Ausstattung</span>
                    <span className="font-medium text-right">
                      {[data.has_elevator && 'Aufzug', data.has_central_heating && 'Zentralheizung', data.has_underground_garage && 'Tiefgarage', data.has_double_parkers && 'Doppelparker', data.has_community_rooms && 'Gemeinschaftsräume', data.has_sub_communities && 'Untergemeinschaften'].filter(Boolean).join(', ') || 'Keine'}
                    </span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    Gebäudezustand
                    <button onClick={() => setStep(1)} className="ml-auto text-xs text-[hsl(var(--brand-blue))] hover:underline" data-testid="summary-edit-1">Bearbeiten</button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Baujahr</span><span className="font-medium">{data.construction_year} ({realAge} Jahre)</span></div>
                  {hasFictiveYear && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Fiktives Baujahr</span><span className="font-medium text-blue-700">{effectiveYear} ({buildingAge} Jahre eff.)</span></div>
                  )}
                  {data.conversion_year && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Umwandlung</span><span className="font-medium">{data.conversion_year}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-muted-foreground">Zustand</span><span className="font-medium">{{sehr_gut:'Sehr gut',gut:'Gut',mittel:'Mittel',schlecht:'Schlecht'}[data.general_condition] || data.general_condition}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Modernisierungen</span>
                    <span className="font-medium">{Object.entries(data.renovations || {}).filter(([k, v]) => k !== 'other' && v).length} durchgeführt</span>
                  </div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Reparaturen</span>
                    <span className="font-medium">{Object.entries(data.major_repairs || {}).filter(([k, v]) => k !== 'other' && v).length} gemeldet</span>
                  </div>
                  {upcomingNeeds.count > 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Sanierungsbedarf</span>
                      <span className="font-medium text-amber-600">{upcomingNeeds.count} Komponente(n)</span>
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    Verwaltungshistorie
                    <button onClick={() => setStep(2)} className="ml-auto text-xs text-[hsl(var(--brand-blue))] hover:underline" data-testid="summary-edit-2">Bearbeiten</button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {(() => {
                    const h = data.history || {};
                    const items = [
                      ['Außeror. Versammlungen', h.extraordinary_meetings],
                      ['Eigentumswechsel', h.ownership_changes],
                      ['Zahlungsverzug', h.payment_defaults],
                      ['Störungen', h.disturbances],
                      ['Gerichtsverfahren', h.court_proceedings],
                    ].filter(([, v]) => v > 0);
                    if (items.length === 0) return <p className="text-muted-foreground">Keine besonderen Vorkommnisse</p>;
                    return items.map(([label, val]) => (
                      <div key={label} className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="font-medium">{val}x</span></div>
                    ));
                  })()}
                  <div className="flex justify-between"><span className="text-muted-foreground">Rücklagen</span><span className="font-medium">{{niedrig:'Niedrig',mittel:'Mittel',hoch:'Hoch'}[data.history?.reserves_level] || 'Mittel'}</span></div>
                  {(data.has_sufficient_reserves === true || data.has_sufficient_reserves === false) && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Für Investitionen</span>
                      <span className={`font-medium ${data.has_sufficient_reserves ? 'text-emerald-600' : 'text-red-600'}`}>
                        {data.has_sufficient_reserves ? 'Ausreichend' : 'Nicht ausreichend'}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-muted-foreground">Hausmeister</span><span className="font-medium">{data.history?.has_professional_caretaker ? 'Ja' : 'Nein'}</span></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    Kontakt & Adresse
                    <button onClick={() => setStep(3)} className="ml-auto text-xs text-[hsl(var(--brand-blue))] hover:underline" data-testid="summary-edit-3">Bearbeiten</button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Ansprechpartner</span><span className="font-medium">{data.contact_name || '–'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">E-Mail</span><span className="font-medium">{data.contact_email || '–'}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Adresse</span><span className="font-medium text-right">{[data.address, data.zip_code, data.city].filter(Boolean).join(', ') || '–'}</span></div>
                </CardContent>
              </Card>
            </div>
            <div className="p-4 bg-emerald-50 border border-emerald-200 text-center" data-testid="summary-ready">
              <p className="text-base text-emerald-800 font-medium">Alle Daten bereit zur Analyse</p>
              <p className="text-sm text-emerald-600 mt-1">Klicken Sie auf "Analyse speichern" um die Auswertung abzuschließen.</p>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between pt-6 border-t">
        <Button variant="ghost" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="gap-2 h-12 text-base" data-testid="wizard-back-btn">
          <ChevronLeft className="h-5 w-5" /> Zurück
        </Button>
        {step === STEPS.length - 1 ? (
          <Button onClick={() => onSubmit?.(data)} disabled={loading} className="gap-2 h-12 px-8 text-base" data-testid="wizard-submit-btn">
            <Save className="h-5 w-5" /> {loading ? 'Speichern...' : (isEdit ? 'Aktualisieren' : 'Analyse speichern')}
          </Button>
        ) : (
          <Button onClick={() => setStep(s => s + 1)} className="gap-2 h-12 px-8 text-base" data-testid="wizard-next-btn">
            Weiter <ChevronRight className="h-5 w-5" />
          </Button>
        )}
      </div>
    </div>
  );
}
